import * as vp from 'vopay';
import * as Yup from 'yup';
import { type FieldData, useForm } from 'vopay';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Title } from 'UI';
import ApprovalTiers, { EMPTY_TIER, type ApprovalTiersRef } from './ApprovalTiers/ApprovalTiers';
import type { UpdateWorkflow } from './types';
import DetailsCard from '@/components/UI/DetailsCard';

const { Box } = vp;

type EditWorkflowFormProps = {
  id: number;
  defaultValues?: UpdateWorkflow;
  isReview: boolean;
  onChange: (id: number, values: UpdateWorkflow, isValid: boolean) => void;
};

const EditWorkflowForm: FunctionComponent<EditWorkflowFormProps> = ({
  defaultValues = {
    WorkflowID: '0',
    NumberOfApprovalsRequired: 0,
    MinAmount: '0',
    MaxAmount: null,
    IsActive: false,
    DateAdded: '',
    LastModified: '',
    TransactionTypes: [],
    tiers: [{ ...EMPTY_TIER, startAmount: '0.00' }],
  },
  id,
  isReview,
  onChange,
}) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'TransactionApproval.Configuration.Workflows.Update.Form',
  });

  const tiersRef = useRef<ApprovalTiersRef | null>(null);
  const [tiersValid, setTiersValid] = useState(false);

  const formConfig = useMemo(
    (): FieldData<UpdateWorkflow>[] => [
      {
        fieldName: 'tiers',
        showField: () => true,
        getInputState: () => 'required',
        validate: () => Yup.array().min(1, 'At least one tier is required'),
        renderField: () => null,
      },
    ],
    [],
  );

  const { ctx } = useForm<UpdateWorkflow>({
    config: formConfig,
    initialValues: { ...defaultValues },
    enableReinitialize: true,
    isReviewing: isReview,
    onSubmit: () => {},
  });

  useEffect(() => {
    onChange(id, ctx.values, ctx.isValid && tiersValid);
  }, [ctx.values, ctx.isValid, tiersValid]);

  return (
    <DetailsCard style={{ width: '100%', padding: '24px' }}>
      <Box flx="col">
        <div>
          <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#5e5e5e' }}>{t('PaymentRails.label')}</p>
          <Title level="4">{ctx.values.TransactionTypes.join('; ')}</Title>
        </div>
        <hr style={{ border: 'none', borderTop: '1px solid #2D2D2D1A', margin: '24px 0' }} />
        <ApprovalTiers
          ref={tiersRef}
          value={ctx.values.tiers}
          onChange={(tiers) => ctx.setFieldValue('tiers', tiers)}
          onValidityChange={setTiersValid}
          disabled={isReview}
        />
      </Box>
    </DetailsCard>
  );
};

export default EditWorkflowForm;
